/**
 * Shared search-filter parsing and backend mapping.
 *
 * Two consumers:
 *   1. /search (`app/(protected)/search/page.tsx`) feeds URL search params
 *      through `parseSearchURLParams` then `buildBackendFilters`.
 *   2. /listen/[slug] (`app/(public)/listen/[slug]/page.tsx`) constructs a
 *      `ParsedSearchParams` directly from a config entry and runs the same
 *      `buildBackendFilters`.
 *
 * SSR and client searches share the interval payload builder. Legacy financial
 * bucket IDs are accepted at boundaries only; age retains discrete spans.
 */

import { z } from 'zod';
import type { FilterParams } from '@/new_components/searchresults/CompanySearchSidebar';
import type { CompanySearchParams } from '@/scripts/search-queries';

/** UI preview state must never form part of a search identity or its filters. */
export function searchParamsWithoutPreview(params: { toString(): string }): URLSearchParams {
  const search = new URLSearchParams(params.toString());
  search.delete('preview');
  return search;
}

/** Retired sorts in URLs and saved searches behave like an absent sort. */
export function normalizeSearchSort(sort: string, keyword = ''): string {
  if (!sort || /^(city|bundesland|industry|cpv)(-|$)/.test(sort)) {
    return keyword ? 'relevance' : 'revenue-desc';
  }
  return sort;
}

export interface ParsedSearchParams {
  q: string;
  /** 'DE' | 'CH' | '' (= global). The selector defaults to DE and always
   *  writes the value, so a saved search records its country explicitly
   *  rather than inheriting whatever the default later becomes. */
  country: string;
  cities: string[];
  registerId: string;
  registerCourt: string;
  registerType: string;
  euid: string;
  hasYearlyMetadata: boolean;
  memberships: string[];
  /**
   * Company lifecycle buckets: active | in_liquidation | insolvent |
   * dissolved | deleted. OR-merged; empty means no filter.
   */
  legalStatus: string[];
  /**
   * HGB §§ 267, 267a size classes: kleinst | klein | mittelgross.
   * OR-merged; empty means no filter. Like `tenderRole` and unlike
   * `legalStatus` these do **not**
   * partition the register — a company is classified only where the register
   * supports it, so selecting all three is a real filter ("has a size class at
   * all"), not a no-op.
   *
   * Three values rather than the four statutory classes: the class is inferred
   * downward from a claimed relief, and no relief asserts largeness, so
   * `gross` is never assigned. The response field on a company may still
   * carry it — rendering a class
   * we are given is not the same as offering it as a filter.
   */
  companySize: string[];
  /**
   * EU public-procurement roles: winner | buyer. OR-merged; empty means no
   * filter. Unlike `legalStatus` these do not partition the register —
   * ~99% of companies are in neither, so selecting both is a real filter
   * ("appears in TED at all"), not a no-op.
   */
  tenderRole: string[];
  /**
   * Founder / family / succession — tri-state, `undefined` means no filter.
   * `false` is a genuine selection: a company whose board has fully turned
   * over. Companies we could not assess are in neither bucket, so these
   * never partition the register.
   */
  familyManaged?: boolean;
  founderLed?: boolean;
  successionRisk?: boolean;
  leiCode: string;
  /** Digits only; `DE` prefix is re-prepended in `buildBackendFilters`. */
  ustIdnr: string;
  hasWebsite: boolean;
  hasEmail: boolean;
  bundesland: string[];
  revenueMin?: number;
  revenueMax?: number;
  /**
   * Widen the revenue interval to also match companies with no revenue on
   * file. Off by default — only 2.6% of companies report revenue, so this
   * turns any interval into ~97% of the database.
   */
  revenueIncludeNull: boolean;
  profitMin?: number;
  profitMax?: number;
  /** Bilanzsumme (balance-sheet total) interval. */
  aktivaMin?: number;
  aktivaMax?: number;
  employeesMin?: number;
  employeesMax?: number;
  naceCodeParams: string[];
  industryParams: string[];
  tags: string[];
  personName: string;
  ageRanges: string[];
  rechtsformCodes: string[];
  cpvCode: string;
  /** 4-digit year string, or '' */
  foundedAtFrom: string;
  /** 4-digit year string, or '' */
  foundedAtTo: string;
  offset: number;
  sort: string;
  limit: number;
  financialYear: number | undefined;
}

/* ────────────────────────── Range definitions ────────────────────────── */

/** A selectable bucket in one of the range filters. `null` means unbounded. */
export interface RangeDefinition {
  id: string;
  min: number | null;
  max: number | null;
}

/**
 * Legacy financial buckets, retained for URL, saved-search and SEO conversion.
 *
 * These used to exist in three places — here (SSR + /listen pages),
 * SearchPageClient (client-side re-search) and CompanySearchSidebar (what
 * the user picks) — and they had drifted: the sidebar offered a
 * `10m-50m` profit bucket that the two mappers split into `10m-20m` /
 * `20m-50m`. An id present in the picker but absent from the mapper
 * resolves to no bounds at all, so choosing that bucket silently applied
 * *no profit filter* and returned the unfiltered set. `lib/` owns them
 * now; the four financial tables are used only to convert old selections.
 */
export const FINANCIAL_RANGES: RangeDefinition[] = [
  { id: '0-100k', min: 0, max: 100000 },
  { id: '100k-1m', min: 100000, max: 1000000 },
  { id: '1m-5m', min: 1000000, max: 5000000 },
  { id: '5m-10m', min: 5000000, max: 10000000 },
  { id: '10m-20m', min: 10000000, max: 20000000 },
  { id: '20m-50m', min: 20000000, max: 50000000 },
  { id: '50m-100m', min: 50000000, max: 100000000 },
  { id: '100m-1b', min: 100000000, max: 1000000000 },
  { id: '1b+', min: 1000000000, max: null },
];

export const PROFIT_RANGES: RangeDefinition[] = [
  { id: 'loss-100m+', min: null, max: -100000000 },
  { id: 'loss-10m-100m', min: -100000000, max: -10000000 },
  { id: 'loss-1m-10m', min: -10000000, max: -1000000 },
  { id: 'loss-100k-1m', min: -1000000, max: -100000 },
  { id: 'loss-0-100k', min: -100000, max: 0 },
  { id: '0-100k', min: 0, max: 100000 },
  { id: '100k-1m', min: 100000, max: 1000000 },
  { id: '1m-5m', min: 1000000, max: 5000000 },
  { id: '5m-10m', min: 5000000, max: 10000000 },
  { id: '10m-50m', min: 10000000, max: 50000000 },
  { id: '50m-100m', min: 50000000, max: 100000000 },
  { id: '100m+', min: 100000000, max: null },
];

/**
 * Bilanzsumme (balance-sheet total) buckets.
 *
 * Same ladder as revenue on purpose — the two sit side by side in the Size
 * & financials group and a user comparing them should not have to re-read
 * the scale. Coverage is the reason this filter exists: 822,065 companies
 * carry a balance-sheet total against 77,126 with revenue, because most
 * German filers publish a balance sheet without a full P&L.
 */
export const AKTIVA_RANGES: RangeDefinition[] = [
  { id: '0-100k', min: 0, max: 100000 },
  { id: '100k-1m', min: 100000, max: 1000000 },
  { id: '1m-5m', min: 1000000, max: 5000000 },
  { id: '5m-10m', min: 5000000, max: 10000000 },
  { id: '10m-20m', min: 10000000, max: 20000000 },
  { id: '20m-50m', min: 20000000, max: 50000000 },
  { id: '50m-100m', min: 50000000, max: 100000000 },
  { id: '100m-1b', min: 100000000, max: 1000000000 },
  { id: '1b+', min: 1000000000, max: null },
];

export const EMPLOYEE_RANGES: RangeDefinition[] = [
  { id: 'emp-0-10', min: 0, max: 10 },
  { id: 'emp-10-50', min: 10, max: 50 },
  { id: 'emp-50-100', min: 50, max: 100 },
  { id: 'emp-100-500', min: 100, max: 500 },
  { id: 'emp-500-1000', min: 500, max: 1000 },
  { id: 'emp-1000+', min: 1000, max: null },
];

/** Legacy financial buckets are envelopes now; age retains discrete spans. */
export function legacyRangesToBounds(ids: string[], table: readonly RangeDefinition[]): { min?: number; max?: number } {
  const known = table.filter((r) => ids.includes(r.id));
  if (!known.length) return {};
  return {
    ...(!known.some((r) => r.min === null) && { min: Math.min(...known.map((r) => r.min!)) }),
    ...(!known.some((r) => r.max === null) && { max: Math.max(...known.map((r) => r.max!)) }),
  };
}

export function parseBound(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && !value.trim()) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

export function parseBounds(minValue: unknown, maxValue: unknown): { min?: number; max?: number } {
  const min = parseBound(minValue);
  const max = parseBound(maxValue);
  return min !== undefined && max !== undefined && min > max ? { min: max, max: min } : { min, max };
}

const LEGACY_METRICS = [
  ['revenue', 'revenueRanges', 'revenueRange', FINANCIAL_RANGES],
  ['aktiva', 'aktivaRanges', 'aktivaRange', AKTIVA_RANGES],
  ['profit', 'profitRanges', 'profitRange', PROFIT_RANGES],
  ['employees', 'employeeRanges', 'employeeRange', EMPLOYEE_RANGES],
] as const;

/** A metric's new URL params win as a pair, including empty/invalid params. */
export function parseMetricURLBounds(params: Record<string, string | string[] | undefined>): Partial<FilterParams> {
  const out: Partial<FilterParams> = {};
  for (const [metric, , legacy, table] of LEGACY_METRICS) {
    const minKey = `${metric}Min` as const;
    const maxKey = `${metric}Max` as const;
    const bounds = params[minKey] !== undefined || params[maxKey] !== undefined
      ? parseBounds(getParam(params, minKey), getParam(params, maxKey))
      : legacyRangesToBounds(getParamAll(params, legacy), table);
    out[minKey] = bounds.min;
    out[maxKey] = bounds.max;
  }
  return out;
}

/** Optional legacy keys are only accepted at stored/config data boundaries. */
export interface LegacyFinancialFilters {
  revenueRanges?: string[];
  aktivaRanges?: string[];
  profitRanges?: string[];
  employeeRanges?: string[];
}

/**
 * Person-age buckets, expressed as the *birth-year* window they select —
 * the backing column is `mv_search.avg_person_birth_year`, so an older age
 * band maps to a lower year range. Derived from the current year at call
 * time rather than module load, so a long-lived process doesn't drift
 * across a New Year boundary.
 */
export function personAgeRanges(
  currentYear: number = new Date().getFullYear(),
): RangeDefinition[] {
  return [
    { id: 'age-20-30', min: currentYear - 30, max: currentYear - 20 },
    { id: 'age-30-40', min: currentYear - 40, max: currentYear - 30 },
    { id: 'age-40-50', min: currentYear - 50, max: currentYear - 40 },
    { id: 'age-50-60', min: currentYear - 60, max: currentYear - 50 },
    { id: 'age-60-70', min: currentYear - 70, max: currentYear - 60 },
    { id: 'age-70+', min: null, max: currentYear - 70 },
  ];
}

/**
 * Turn the selected bucket ids into the discrete spans the backend
 * OR-merges (`Range.spans`).
 *
 * This used to collapse the selection into a single {min, max} envelope,
 * which is only correct when the picked buckets happen to be adjacent.
 * Selecting "0 – 100k" and "1b+" produced `{min: 0, max: null}` — every
 * company with any revenue at all (2,365,440 rows on prod) rather than the
 * 6,395 in those two bands. Sending the bands separately removes the class
 * of bug rather than special-casing it. Financial sliders now deliberately
 * use an envelope when converting legacy IDs; only age still uses this path.
 */
export function getSpans(
  rangeIds: string[],
  ranges: readonly RangeDefinition[],
): { gte?: number; lte?: number }[] | undefined {
  if (rangeIds.length === 0) return undefined;
  const spans = ranges
    .filter((r) => rangeIds.includes(r.id))
    .filter((r) => r.min !== null || r.max !== null)
    .map((r) => ({
      ...(r.min !== null && { gte: r.min }),
      ...(r.max !== null && { lte: r.max }),
    }));
  return spans.length > 0 ? spans : undefined;
}

/**
 * A financial year counts as a filter only when it is a plausible year.
 *
 * The sidebar used to clear the picker by passing `0` as a "cleared"
 * sentinel. Zero survives every `!= null` / `!== undefined` check on the way
 * out, so removing the filter wrote `year=0` into the URL and asked the
 * backend for companies that filed for year zero — of which there are none.
 * Clearing now passes `undefined`; this guard is the second line of defence,
 * so links already shared with `year=0` recover instead of returning nothing.
 */
export function sanitizeFinancialYear(value: unknown): number | undefined {
  const n =
    typeof value === 'string' ? Number.parseInt(value, 10) : (value as number);
  if (!Number.isInteger(n)) return undefined;
  // Bundesanzeiger filings start in the 2000s; allow next year for early filers.
  if (n < 1900 || n > new Date().getFullYear() + 1) return undefined;
  return n;
}

/* ─────────────────────────── URL param helpers ─────────────────────────── */

function getParam(
  params: Record<string, string | string[] | undefined>,
  key: string,
): string {
  const val = params[key];
  if (Array.isArray(val)) return val[0] || '';
  return val || '';
}

function getParamAll(
  params: Record<string, string | string[] | undefined>,
  key: string,
): string[] {
  const val = params[key];
  if (Array.isArray(val)) return val;
  if (val) return [val];
  return [];
}

/**
 * Parse Next.js `searchParams` into a typed, fully-defaulted shape.
 * The `sort` default mirrors the original /search behavior: when a keyword
 * query is present and no explicit sort is given, sort by relevance; otherwise
 * sort by revenue desc.
 */
/**
 * Read a tri-state boolean out of the query string.
 *
 * Only the literal strings "true" and "false" bind; anything else (absent,
 * empty, junk from a stale bookmark) is `undefined` = no filter. Reading a
 * bare presence as `true` would make `?founderLed=` silently filter.
 */
function parseTriState(
  params: Record<string, string | string[] | undefined>,
  key: string,
): boolean | undefined {
  const raw = params[key];
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

export function parseSearchURLParams(
  params: Record<string, string | string[] | undefined>,
): ParsedSearchParams {
  const q = getParam(params, 'q');
  const personName = getParam(params, 'person_name');
  const sort = normalizeSearchSort(getParam(params, 'sort'), q || personName);
  const yearParam = getParam(params, 'year');

  return {
    q,
    cities: getParamAll(params, 'city'),
    registerId: getParam(params, 'registerId'),
    registerCourt: getParam(params, 'registerCourt'),
    registerType: getParam(params, 'registerType'),
    euid: getParam(params, 'euid'),
    hasYearlyMetadata: getParam(params, 'hasYearlyMetadata') === 'true',
    memberships: getParamAll(params, 'membership'),
    legalStatus: getParamAll(params, 'legalStatus'),
    companySize: getParamAll(params, 'companySize'),
    // Absent URL param = the default (Germany); 'all' is the explicit global.
    country: (() => {
      const c = (getParam(params, 'country') || 'DE').toUpperCase();
      return c === 'ALL' ? '' : c === 'CH' ? 'CH' : 'DE';
    })(),
    tenderRole: getParamAll(params, 'tenderRole'),
    familyManaged: parseTriState(params, 'familyManaged'),
    founderLed: parseTriState(params, 'founderLed'),
    successionRisk: parseTriState(params, 'successionRisk'),
    leiCode: getParam(params, 'leiCode'),
    ustIdnr: getParam(params, 'ustIdnr')
      .replace(/^DE/i, '')
      .replace(/\D/g, ''),
    hasWebsite: getParam(params, 'hasWebsite') === 'true',
    hasEmail: getParam(params, 'hasEmail') === 'true',
    bundesland: getParamAll(params, 'Bundesland'),
    ...parseMetricURLBounds(params),
    revenueIncludeNull: getParam(params, 'revenueIncludeNull') === 'true',
    naceCodeParams: getParamAll(params, 'naceCode'),
    industryParams: getParamAll(params, 'industry'),
    tags: getParamAll(params, 'tag'),
    personName,
    ageRanges: getParamAll(params, 'ageRange'),
    rechtsformCodes: getParamAll(params, 'rechtsformCode'),
    cpvCode: getParam(params, 'cpvCode'),
    foundedAtFrom: getParam(params, 'foundedAtFrom'),
    foundedAtTo: getParam(params, 'foundedAtTo'),
    offset: parseInt(getParam(params, 'offset') || '0', 10) || 0,
    sort,
    limit: parseInt(getParam(params, 'limit') || '50', 10) || 50,
    financialYear: sanitizeFinancialYear(yearParam),
  };
}

/**
 * True when the parsed params represent the unfiltered default landing on
 * /search (no filters, page 1, default sort/limit). The original page short-
 * circuits SSR in this case to keep first paint instant.
 */
/** Keys of ParsedSearchParams that shape the result *page*, not the result
 *  set. Everything else counts as a filter. */
const DISPLAY_KEYS: ReadonlySet<string> = new Set(['offset', 'sort', 'limit']);

export function isDefaultSearch(p: ParsedSearchParams): boolean {
  // Derived over the parsed object rather than a hand-kept list: the list
  // had drifted — every filter added later (registerType, hasYearlyMetadata,
  // memberships, legalStatus, tenderRole, the founder tri-states, leiCode,
  // ustIdnr, hasWebsite, hasEmail, revenueIncludeNull) was missing, so a URL
  // carrying only one of them skipped SSR and the client mount fetch then
  // showed the *unfiltered* default list under a filtered URL.
  const hasFilters = Object.entries(p).some(([key, value]) => {
    if (DISPLAY_KEYS.has(key)) return false;
    // A selected negative tri-state is still a filter (unlike the ordinary
    // false booleans, which are the default for website/email flags).
    if (TRI_STATE_KEYS.has(key)) return value !== undefined;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value;
    return value != null && value !== '';
  });
  return (
    !hasFilters && p.offset === 0 && p.sort === 'revenue-desc' && p.limit === 50
  );
}

/**
 * Build the `CompanySearchParams` object passed to `searchCompanies()`. The
 * order of keys and conditional spreads mirrors the original inline code so
 * the resulting backend call is byte-equivalent.
 */
export function buildBackendFilters(
  p: ParsedSearchParams,
  opts: { naceCodes?: readonly { code: string; level: number }[] } = {},
): CompanySearchParams {
  const naceCodes = opts.naceCodes ?? [];

  const rev = boundsToRange(p.revenueMin, p.revenueMax);
  const prof = boundsToRange(p.profitMin, p.profitMax);
  const akt = boundsToRange(p.aktivaMin, p.aktivaMax);
  const emp = boundsToRange(p.employeesMin, p.employeesMax);
  // Age bands go through the same discrete-span path — picking "20–30"
  // and "60–70" must mean those two cohorts, not the single 20-to-70
  // window a merged min/max would produce.
  const age = getSpans(p.ageRanges, personAgeRanges());

  const codeToLevel = new Map(naceCodes.map((c) => [c.code, c.level]));
  const naceSectors = p.naceCodeParams.filter(
    (code) => codeToLevel.get(code) === 1,
  );
  const naceSubsectors = p.naceCodeParams.filter(
    (code) => codeToLevel.get(code) !== 1,
  );

  // foundedAtFrom / foundedAtTo arrive as 4-digit year strings. Lower bound
  // becomes Jan 1, upper bound becomes Dec 31 so both ends are inclusive.
  const foundedAt: { gte?: string; lte?: string } = {};
  if (/^\d{4}$/.test(p.foundedAtFrom))
    foundedAt.gte = `${p.foundedAtFrom}-01-01`;
  if (/^\d{4}$/.test(p.foundedAtTo)) foundedAt.lte = `${p.foundedAtTo}-12-31`;

  return {
    ...(p.q && { q: p.q }),
    city: p.cities.join(',') || undefined,
    registerId: p.registerId,
    registerCourt: p.registerCourt,
    ...(p.registerType && { registerType: p.registerType }),
    euid: p.euid || undefined,
    Bundesland: p.bundesland,
    ...(p.hasYearlyMetadata && { hasYearlyMetadata: true }),
    limit: p.limit,
    offset: p.offset,
    sort: p.sort,
    // `includeNull` is opt-in and only offered for revenue — 97.4% of
    // companies have no revenue on file, so folding NULLs in silently
    // (which the backend used to do for every bucket up to 5M) turned a
    // narrow bucket into "match everything".
    ...(rev && { revenue: { ...rev, ...(p.revenueIncludeNull && { includeNull: true }) } }),
    ...(prof && { profit: prof }),
    ...(akt && { aktiva: akt }),
    ...(emp && { employeeCount: emp }),
    naceSectors,
    naceSubsectors,
    ...(p.industryParams.length > 0 && { industries: p.industryParams }),
    ...(sanitizeFinancialYear(p.financialYear) != null && {
      financialYear: p.financialYear,
    }),
    ...(Object.keys(foundedAt).length > 0 && { foundedAt }),
    ...(p.tags.length > 0 && { tags: p.tags }),
    ...(p.personName && { personName: p.personName }),
    ...(age && { avgBirthYear: { spans: age } }),
    ...(p.rechtsformCodes.length > 0 && {
      rechtsformCodes: p.rechtsformCodes.flatMap((c) => c.split(',')),
    }),
    ...(p.cpvCode && { cpvCode: p.cpvCode }),
    ...(p.memberships.length > 0 && { memberships: p.memberships }),
    ...(p.legalStatus.length > 0 && { legalStatus: p.legalStatus }),
    ...(p.companySize.length > 0 && { companySize: p.companySize }),
    ...(p.country && { country: p.country }),
    ...(p.tenderRole.length > 0 && { tenderRole: p.tenderRole }),
    ...(p.familyManaged !== undefined && { familyManaged: p.familyManaged }),
    ...(p.founderLed !== undefined && { founderLed: p.founderLed }),
    ...(p.successionRisk !== undefined && { successionRisk: p.successionRisk }),
    ...(p.leiCode && { leiCode: p.leiCode }),
    ...(p.ustIdnr && { ustIdnr: `DE${p.ustIdnr}` }),
    ...(p.hasWebsite && { hasWebsite: true }),
    ...(p.hasEmail && { hasEmail: true }),
  };
}

/**
 * Zeroed defaults for `ParsedSearchParams`. Call this and override the
 * specific fields you need. Useful for /listen/[slug] which builds filters
 * from a config object rather than URL params.
 */
export function emptyParsedSearchParams(): ParsedSearchParams {
  return {
    q: '',
    cities: [],
    registerId: '',
    registerCourt: '',
    registerType: '',
    euid: '',
    hasYearlyMetadata: false,
    memberships: [],
    legalStatus: [],
    companySize: [],
    country: 'DE',
    tenderRole: [],
    familyManaged: undefined,
    founderLed: undefined,
    successionRisk: undefined,
    leiCode: '',
    ustIdnr: '',
    hasWebsite: false,
    hasEmail: false,
    bundesland: [],
    revenueMin: undefined,
    revenueMax: undefined,
    revenueIncludeNull: false,
    profitMin: undefined,
    profitMax: undefined,
    aktivaMin: undefined,
    aktivaMax: undefined,
    employeesMin: undefined,
    employeesMax: undefined,
    naceCodeParams: [],
    industryParams: [],
    tags: [],
    personName: '',
    ageRanges: [],
    rechtsformCodes: [],
    cpvCode: '',
    foundedAtFrom: '',
    foundedAtTo: '',
    offset: 0,
    sort: 'revenue-desc',
    limit: 50,
    financialYear: undefined,
  };
}

/**
 * A filter object reduced to its meaning, for equality comparison.
 *
 * Two searches are the same search when they narrow the register the same
 * way — regardless of key order, of empty arrays versus absent keys, or of
 * `false` versus `undefined` on the boolean switches. That equivalence is
 * what lets `/search` tell the user "you already saved this" and stops the
 * same query being saved twice under two names.
 *
 * Deliberately *not* a hash: hashing would need the same canonicalisation on
 * the backend, and a second implementation of "is this the same search" is
 * exactly the kind of thing that drifts. A JSON compare against the searches
 * the client already fetched needs only this one function.
 */
export function canonicalFilters(filters: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // Selection order is not meaning: picking Bayern then Hessen is the
      // same search as Hessen then Bayern.
      out[key] = [...value].map(String).sort();
      continue;
    }
    // `false` on a switch means "not applied", the same as absent — but a
    // tri-state `false` (familyManaged=false) is a real filter, so only the
    // plain booleans that have no negative form are dropped.
    if (value === false && !TRI_STATE_KEYS.has(key)) continue;
    out[key] = value;
  }
  return JSON.stringify(out);
}

/** Filters where `false` selects companies rather than disabling the filter. */
const TRI_STATE_KEYS = new Set(['familyManaged', 'founderLed', 'successionRisk']);

/** Interval payload, shared by SSR and client searches. */
export function boundsToRange(min?: number, max?: number): { gte?: number; lte?: number } | undefined {
  if (min === undefined && max === undefined) return undefined;
  return { ...(min !== undefined && { gte: min }), ...(max !== undefined && { lte: max }) };
}

const storedFilterSchema = z.object({
  q: z.string().catch(''),
  country: z.string().catch('DE'),
  city: z.array(z.string()).catch([]),
  registerId: z.string().catch(''),
  registerCourt: z.string().catch(''),
  registerType: z.string().catch(''),
  euid: z.string().catch(''),
  Bundesland: z.array(z.string()).catch([]),
  revenueMin: z.preprocess(parseBound, z.number().optional()),
  revenueMax: z.preprocess(parseBound, z.number().optional()),
  revenueIncludeNull: z.boolean().catch(false),
  profitMin: z.preprocess(parseBound, z.number().optional()),
  profitMax: z.preprocess(parseBound, z.number().optional()),
  aktivaMin: z.preprocess(parseBound, z.number().optional()),
  aktivaMax: z.preprocess(parseBound, z.number().optional()),
  financialYear: z.preprocess(sanitizeFinancialYear, z.number().optional()),
  employeesMin: z.preprocess(parseBound, z.number().optional()),
  employeesMax: z.preprocess(parseBound, z.number().optional()),
  ageRanges: z.array(z.string()).catch([]),
  naceCodes: z.array(z.string()).catch([]),
  industries: z.array(z.string()).catch([]),
  foundedAtFrom: z.string().catch(''),
  foundedAtTo: z.string().catch(''),
  tags: z.array(z.string()).catch([]),
  personName: z.string().catch(''),
  rechtsformCodes: z.array(z.string()).catch([]),
  cpvCode: z.string().catch(''),
  hasYearlyMetadata: z.boolean().catch(false),
  memberships: z.array(z.string()).catch([]),
  legalStatus: z.array(z.string()).catch([]),
  companySize: z.array(z.string()).catch([]),
  tenderRole: z.array(z.string()).catch([]),
  familyManaged: z.boolean().optional().catch(undefined),
  founderLed: z.boolean().optional().catch(undefined),
  successionRisk: z.boolean().optional().catch(undefined),
  leiCode: z.string().catch(''),
  ustIdnr: z.string().catch(''),
  hasWebsite: z.boolean().catch(false),
  hasEmail: z.boolean().catch(false),
});

/** Normalize once when saved/legacy filters enter the app; never retain bucket keys. */
export function normalizeFilterParams(raw: unknown): FilterParams {
  const record = z.record(z.string(), z.unknown()).safeParse(raw);
  const input = record.success ? { ...record.data } : {};
  for (const [metric, legacy, , table] of LEGACY_METRICS) {
    const minKey = `${metric}Min` as const;
    const maxKey = `${metric}Max` as const;
    if (!(minKey in input) && !(maxKey in input)) {
      const ids = z.array(z.string()).safeParse(input[legacy]);
      const bounds = legacyRangesToBounds(ids.success ? ids.data : [], table);
      input[minKey] = bounds.min;
      input[maxKey] = bounds.max;
    }
    const bounds = parseBounds(input[minKey], input[maxKey]);
    input[minKey] = bounds.min;
    input[maxKey] = bounds.max;
    delete input[legacy];
  }
  return storedFilterSchema.parse(input);
}
